import { motion } from 'framer-motion';
import useGameStore from '@/store/gameStore';

function ReviewPhase() {
  const { gameState, isGuesser } = useGameStore();

  if (!gameState?.round) return null;

  const isCurrentGuesser = isGuesser();
  const clues = Object.entries(gameState.round.clues);

  const getClueStatusStyle = (status: string) => {
    switch (status) {
      case 'REMOVED_DUPLICATE':
        return 'clue-card-duplicate';
      case 'REMOVED_ILLEGAL':
        return 'clue-card-illegal';
      default:
        return 'clue-card-visible';
    }
  };

  const getClueStatusIcon = (status: string) => {
    switch (status) {
      case 'REMOVED_DUPLICATE':
        return '🔁';
      case 'REMOVED_ILLEGAL':
        return '⛔';
      default:
        return '✅';
    }
  };

  if (isCurrentGuesser) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Reviewing Clues...</h2>
          <p className="text-gray-600">The AI is checking for duplicates and invalid clues</p>

          <motion.div
            className="mt-8 flex justify-center gap-2"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-3 h-3 bg-primary-500 rounded-full" />
            ))}
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Review Phase</h2>

      <div className="text-center mb-6">
        <p className="text-sm text-gray-500 mb-2">Target word:</p>
        <p className="text-2xl font-bold text-primary-600">{gameState.round.targetWord}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {clues.map(([userId, clue], index) => (
          <motion.div
            key={userId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            data-testid={`card-clue-${userId}`}
            className={getClueStatusStyle(clue.status)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-semibold text-lg">{clue.text}</p>
                <p className="text-xs text-gray-500 mt-1">{clue.authorName}</p>
              </div>
              <span className="text-lg" title={clue.status}>
                {getClueStatusIcon(clue.status)}
              </span>
            </div>
            {clue.status !== 'VISIBLE' && (
              <p className="text-xs mt-2 text-danger-600">
                {clue.status === 'REMOVED_DUPLICATE' && 'Duplicate'}
                {clue.status === 'REMOVED_ILLEGAL' && 'Invalid'}
              </p>
            )}
          </motion.div>
        ))}
      </div>

      <div className="text-center">
        <p className="text-gray-600">
          <span className="font-semibold">
            {clues.filter(([_, c]) => c.status === 'VISIBLE').length}
          </span>{' '}
          valid clues will be shown to the guesser
        </p>
      </div>

      <motion.div
        className="mt-8 p-4 bg-yellow-50 rounded-lg text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <p className="text-yellow-700">⏱️ Moving to guessing phase soon...</p>
      </motion.div>
    </div>
  );
}

export default ReviewPhase;
